import { useEffect, useReducer } from 'react';
import type { MediaItem, MessageItem, PublicSession, ServerMessage } from '@wallflower/shared';
import { WS_HEARTBEAT_INTERVAL_MS } from '@wallflower/shared';
import { api } from './api.ts';

export type ConnectionStatus = 'connecting' | 'live' | 'reconnecting' | 'gone';

export interface SocketState {
  status: ConnectionStatus;
  session: PublicSession | null;
  media: MediaItem[];
  messages: MessageItem[];
  isAdmin: boolean;
  /** True while state is being kept fresh by polling because the socket won't hold. */
  degraded: boolean;
}

interface Snapshot {
  session: PublicSession;
  media: MediaItem[];
  messages: MessageItem[];
  isAdmin: boolean;
}

type Action =
  | { kind: 'status'; status: ConnectionStatus }
  | { kind: 'degraded'; degraded: boolean }
  | { kind: 'snapshot'; snapshot: Snapshot }
  | { kind: 'message'; message: ServerMessage };

const initialState: SocketState = {
  status: 'connecting',
  session: null,
  media: [],
  messages: [],
  isAdmin: false,
  degraded: false,
};

function byNewest(a: { createdAt: number }, b: { createdAt: number }): number {
  return b.createdAt - a.createdAt;
}

function reduce(state: SocketState, action: Action): SocketState {
  if (action.kind === 'status') return { ...state, status: action.status };
  if (action.kind === 'degraded') return { ...state, degraded: action.degraded };

  if (action.kind === 'snapshot') {
    const { snapshot } = action;
    return {
      ...state,
      status: 'live',
      session: snapshot.session,
      media: [...snapshot.media].sort(byNewest),
      messages: [...snapshot.messages].sort(byNewest),
      isAdmin: snapshot.isAdmin,
    };
  }

  const message = action.message;
  switch (message.type) {
    case 'hello':
      return {
        ...state,
        status: 'live',
        session: message.session,
        media: [...message.media].sort(byNewest),
        messages: [...message.messages].sort(byNewest),
        isAdmin: message.isAdmin,
      };
    case 'settings:updated':
      return state.session
        ? { ...state, session: { ...state.session, settings: message.settings } }
        : state;
    // One frame carrying everything a plan decides, so no screen ever renders a
    // half-applied upgrade.
    case 'plan:updated':
      return state.session
        ? {
            ...state,
            session: {
              ...state.session,
              plan: message.plan,
              limits: message.limits,
              expiresAt: message.expiresAt,
              settings: message.settings,
            },
          }
        : state;
    case 'media:added':
    case 'media:updated': {
      const others = state.media.filter((m) => m.id !== message.media.id);
      return { ...state, media: [message.media, ...others].sort(byNewest) };
    }
    case 'media:hidden':
      // The slideshow drops hidden items entirely; admins keep them, flagged.
      return {
        ...state,
        media: state.isAdmin
          ? state.media.map((m) => (m.id === message.mediaId ? { ...m, hidden: true } : m))
          : state.media.filter((m) => m.id !== message.mediaId),
      };
    case 'media:restored':
      return {
        ...state,
        media: state.media.map((m) =>
          m.id === message.mediaId ? { ...m, hidden: false } : m,
        ),
      };
    case 'media:deleted':
      return { ...state, media: state.media.filter((m) => m.id !== message.mediaId) };
    case 'message:added':
    case 'message:restored': {
      const others = state.messages.filter((m) => m.id !== message.message.id);
      return { ...state, messages: [message.message, ...others].sort(byNewest) };
    }
    case 'message:hidden':
      return {
        ...state,
        messages: state.isAdmin
          ? state.messages.map((m) =>
              m.id === message.messageId ? { ...m, hidden: true } : m,
            )
          : state.messages.filter((m) => m.id !== message.messageId),
      };
    case 'message:deleted':
      return {
        ...state,
        messages: state.messages.filter((m) => m.id !== message.messageId),
      };
    case 'session:deleted':
      return { ...state, status: 'gone', media: [], messages: [] };
    case 'ping':
      return state;
    default:
      return state;
  }
}

const MAX_BACKOFF_MS = 30_000;
/**
 * The server pings every 30s. If nothing at all arrives for this long the link
 * is dead even though the browser hasn't been told - the usual case is a NAT or
 * proxy dropping an idle connection without sending a FIN, which leaves the
 * socket looking open forever.
 */
const SILENCE_TIMEOUT_MS = WS_HEARTBEAT_INTERVAL_MS * 2.5;
const WATCHDOG_INTERVAL_MS = 5_000;
/**
 * How long the wake-up probe waits for proof of life. The server answers a
 * client `ping` immediately, so this only has to cover a slow round trip - but
 * it also has to outlast one full heartbeat, so that a client talking to an
 * older server (which won't answer) waits for the next scheduled ping instead
 * of tearing down a link that is perfectly healthy.
 */
const PROBE_GRACE_MS = WS_HEARTBEAT_INTERVAL_MS + 5_000;
/** After this many failed attempts, keep the screen fresh over plain HTTP. */
const FAILURES_BEFORE_POLLING = 3;
const POLL_INTERVAL_MS = 10_000;

/**
 * Live session state over one WebSocket, built for a TV left running all night
 * on venue wifi. It re-seeds from `hello` on every (re)connect, notices a
 * silently dead link, reconnects the moment the network or tab comes back, and
 * falls back to polling if the socket cannot be held at all.
 */
export function useSessionSocket(params: {
  sessionId?: string;
  adminToken?: string;
}): SocketState {
  const [state, dispatch] = useReducer(reduce, initialState);
  const { sessionId, adminToken } = params;

  useEffect(() => {
    if (!sessionId && !adminToken) return;

    let socket: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let disposed = false;
    let attempts = 0;
    let lastFrameAt = Date.now();

    const fetchSnapshot = async (): Promise<void> => {
      try {
        if (adminToken) {
          const result = await api.adminSession(adminToken);
          dispatch({
            kind: 'snapshot',
            snapshot: {
              session: result.session,
              media: result.media,
              messages: result.messages,
              isAdmin: true,
            },
          });
        } else if (sessionId) {
          const [session, media, messages] = await Promise.all([
            api.publicSession(sessionId),
            api.publicMedia(sessionId),
            api.publicMessages(sessionId),
          ]);
          dispatch({
            kind: 'snapshot',
            snapshot: {
              session,
              media: media.media,
              messages: messages.messages,
              isAdmin: false,
            },
          });
        }
      } catch {
        // Offline or the server is down; the next tick tries again.
      }
    };

    const startPolling = () => {
      if (pollTimer || disposed) return;
      dispatch({ kind: 'degraded', degraded: true });
      void fetchSnapshot();
      pollTimer = setInterval(() => void fetchSnapshot(), POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (!pollTimer) return;
      clearInterval(pollTimer);
      pollTimer = undefined;
      dispatch({ kind: 'degraded', degraded: false });
    };

    const scheduleReconnect = () => {
      if (disposed || retryTimer) return;
      // Jitter keeps a room full of screens from reconnecting in lockstep.
      const base = Math.min(1000 * 2 ** attempts, MAX_BACKOFF_MS);
      const delay = base * (0.75 + Math.random() * 0.5);
      attempts += 1;
      if (attempts >= FAILURES_BEFORE_POLLING) startPolling();
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        connect();
      }, delay);
    };

    const connect = () => {
      if (disposed) return;
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        return;
      }

      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const query = adminToken
        ? `adminToken=${encodeURIComponent(adminToken)}`
        : `sessionId=${encodeURIComponent(sessionId ?? '')}`;

      const next = new WebSocket(`${protocol}://${window.location.host}/ws?${query}`);
      socket = next;
      lastFrameAt = Date.now();

      next.addEventListener('message', (event) => {
        lastFrameAt = Date.now();
        const message = JSON.parse(event.data as string) as ServerMessage;
        if (message.type === 'ping') {
          if (next.readyState === WebSocket.OPEN) next.send(JSON.stringify({ type: 'pong' }));
          return;
        }
        // The answer to our wake-up probe. Carries nothing: `lastFrameAt` above
        // has already been pushed forward, which is all it was sent for.
        if (message.type === 'pong') return;
        dispatch({ kind: 'message', message });
      });

      next.addEventListener('open', () => {
        lastFrameAt = Date.now();
        attempts = 0;
        stopPolling();
        dispatch({ kind: 'status', status: 'live' });
      });

      next.addEventListener('close', (event) => {
        if (disposed || socket !== next) return;
        // 4404: the session is gone for good, so stop trying.
        if (event.code === 4404) {
          stopPolling();
          dispatch({ kind: 'status', status: 'gone' });
          return;
        }
        dispatch({ kind: 'status', status: 'reconnecting' });
        scheduleReconnect();
      });

      next.addEventListener('error', () => {
        // Firefox fires error without close in some cases; closing normalises it.
        if (next.readyState !== WebSocket.CLOSED) next.close();
      });
    };

    // Nothing heard for too long means the link is dead, whatever the browser
    // thinks. Closing it makes the close handler reconnect through the usual path.
    const watchdog = setInterval(() => {
      if (disposed || !socket) return;
      if (socket.readyState !== WebSocket.OPEN) return;
      if (Date.now() - lastFrameAt > SILENCE_TIMEOUT_MS) socket.close(4000, 'silent link');
    }, WATCHDOG_INTERVAL_MS);

    // A TV waking up, or wifi returning, should not wait out the backoff.
    const reconnectNow = () => {
      if (disposed) return;
      if (socket && socket.readyState === WebSocket.OPEN) {
        // Confirm the link is really alive rather than assuming it: ask the
        // server to answer now. Its `pong` lands in the message handler and
        // pushes `lastFrameAt` forward, which is what clears the deadline below.
        socket.send(JSON.stringify({ type: 'ping' }));
        lastFrameAt = Math.min(lastFrameAt, Date.now() - SILENCE_TIMEOUT_MS + PROBE_GRACE_MS);
        return;
      }
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = undefined;
      }
      attempts = 0;
      connect();
      void fetchSnapshot();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') reconnectNow();
    };

    window.addEventListener('online', reconnectNow);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', reconnectNow);

    connect();

    return () => {
      disposed = true;
      clearInterval(watchdog);
      if (retryTimer) clearTimeout(retryTimer);
      if (pollTimer) clearInterval(pollTimer);
      window.removeEventListener('online', reconnectNow);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', reconnectNow);
      socket?.close();
    };
  }, [sessionId, adminToken]);

  return state;
}
